import {useCallback, useEffect, useRef, useState} from 'react'

// Both lists keep visited pages stable until an explicit refresh.
const useEmailPages = (fetchPage, enabled = true) => {
  const [pages, setPages] = useState([])
  const [pageIndex, setPageIndex] = useState(0)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState(null)
  const requestId = useRef(0)
  const retryRequest = useRef({index: 0, cursor: null, reset: true})

  const load = useCallback(async (index, cursor = null, reset = false) => {
    if(!enabled) return
    const currentRequest = ++requestId.current
    retryRequest.current = {index, cursor, reset}
    setLoading(true)
    setError(null)
    if(reset) {
      setPages([])
      setPageIndex(0)
    }
    try {
      const page = await fetchPage(cursor)
      if(currentRequest !== requestId.current) return
      setPages(previous => {
        const next = reset ? [] : [...previous]
        next[index] = page
        return next
      })
      setPageIndex(index)
    } catch(err) {
      if(currentRequest === requestId.current) setError(err)
    } finally {
      if(currentRequest === requestId.current) setLoading(false)
    }
  }, [enabled, fetchPage])

  useEffect(() => {
    if(enabled) load(0, null, true)
    return () => { requestId.current += 1 }
  }, [enabled, load])

  const goTo = index => {
    if(loading || index < 0) return
    if(pages[index]) {
      setError(null)
      setPageIndex(index)
    } else if(index === pageIndex + 1 && pages[pageIndex]?.hasNext) {
      load(index, pages[pageIndex].nextCursor)
    }
  }

  return {
    page: pages[pageIndex], pageIndex, loading, error, goTo,
    refresh: () => load(0, null, true),
    retry: () => {
      const {index, cursor, reset} = retryRequest.current
      load(index, cursor, reset)
    },
  }
}

export default useEmailPages
